/**
 * Certificates, as the platform keeps them (build 2).
 *
 * A certificate is a library document whose type the pack files under the
 * `certificate` category, and each issue of it — the first, then every
 * renewal — is a revision. So a renewal never overwrites the issue before it,
 * and "was it valid on the fourteenth" stays answerable. The certification
 * boards add a certificate as `asset_certificate` (the airports pack), and
 * read every certificate the library holds, so one added from the library
 * — the aerodrome certificate, a calibration — shows on the boards too.
 *
 * The register's own fields (the asset, the kind, the accountable post, the
 * programmes behind it, the evidence expected) ride in the document's attrs.
 * Who issued a particular issue rides in that revision's attrs.
 *
 * Imports nothing at run time: the entry chunk reaches this through the
 * DataSource.
 */
import type { DocumentRow, RevisionStatus } from "./library";

/** The type the certification boards record into. */
export const CERTIFICATE_TYPE = "asset_certificate";

/**
 * Whether a library document is a certificate.
 *
 * By the type's category, which is what the platform files it under. The
 * literal type key is the demo's own (its type IS called "certificate"), and
 * the tag is what a platform older than the category column still sends —
 * the pack tags its certificate types "certificate".
 */
export function isCertificate(d: Pick<DocumentRow, "type" | "category" | "tags">): boolean {
  return d.category === "certificate" || d.type === "certificate" || (d.tags ?? []).includes("certificate");
}

/** What the register keeps on the certificate's document. */
export interface CertificateAttrs {
  register: "certification";
  /** statutory · regulatory · calibration · type · competence · warranty */
  kind: string;
  asset_tag?: string;
  asset_name: string;
  /** The accountable post, as text — not a person. */
  owner?: string;
  /** The issuing body the certificate names. */
  body?: string;
  domain?: string;
  /** The maintenance programmes the certificate rests on. */
  needs?: string[];
  /** The evidence the certificate says should exist. */
  evidence?: string[];
}

/** One issue of a certificate: the first, or a renewal. */
export interface CertificateIssueInput {
  /** The reference printed on this issue — the revision's label. */
  revision: string;
  /** ISO date. */
  issuedOn?: string;
  /** ISO date. Required: a certificate with no expiry is one nobody notices lapsing. */
  expiresOn: string;
  changeNote?: string;
  /** Who issued this one, when it is not the body the certificate names. */
  issuedBy?: string;
  file?: File;
  /** Put it in force at once. Only for someone who holds document:approve; the platform decides. */
  issueNow: boolean;
}

export interface NewCertificateInput {
  siteId: string;
  /** Defaults to `asset_certificate`. */
  type?: string;
  title: string;
  /** The document's reference; left out when the form had none. */
  reference?: string;
  tags: string[];
  attrs: CertificateAttrs;
  issue: CertificateIssueInput;
}

/** Why a recorded issue is not in force yet. */
export type CertificateWaiting =
  /** nobody who may approve recorded it */
  | "approver"
  /** the type requires review before issue (the aerodrome certificate) */
  | "review";

/** How a certificate write landed. */
export interface CertificateWrite {
  documentId: string;
  revisionId: string;
  /** The issue's label. */
  revision: string;
  status: RevisionStatus;
  /** In force now. */
  issued: boolean;
  waiting?: CertificateWaiting;
  /** The platform's own words, when it declined to put the issue in force. */
  why?: string;
}

export interface AttachedFile {
  revisionId: string;
  bytes?: number;
  contentType?: string;
}
